import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  SelectChangeEvent,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../utils/api';

interface PurchaseStat {
  _id: string;
  productType: string;
  item: string;
  title: string;
  description: string;
  image: string;
  totalPurchases: number;
  totalRevenue: number;
  averagePrice: number;
  lastPurchaseDate: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiResponse {
  success: boolean;
  data: PurchaseStat[];
  pagination: Pagination;
  summary: {
    totalPurchases: number;
    totalRevenue: number;
    averageOrderValue: number;
    uniqueProducts: number;
  };
}

export const PurchaseStats: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<PurchaseStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 0,
    limit: 10,
    pages: 0
  });
  const [sortBy, setSortBy] = useState('totalPurchases');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [summary, setSummary] = useState({
    totalPurchases: 0,
    totalRevenue: 0,
    averageOrderValue: 0,
    uniqueProducts: 0
  });

  const fetchStats = async (page: number, limit: number, sortBy: string, sortOrder: string, startDate?: Date | null, endDate?: Date | null) => {
    try {
      setLoading(true);
      let url = `/orders/stats?page=${page + 1}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      
      if (startDate) {
        url += `&startDate=${startDate.toISOString()}`;
      }
      if (endDate) {
        url += `&endDate=${endDate.toISOString()}`;
      }
      
      const response = await api.get<ApiResponse>(url);
      setStats(response.data.data);
      setPagination(response.data.pagination);
      setSummary(response.data.summary);
      setError(null);
    } catch (err) {
      setError(t('admin.purchaseStats.error'));
      console.error('Error fetching purchase stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(0, 10, sortBy, sortOrder, startDate, endDate);
  }, [sortBy, sortOrder, startDate, endDate]);

  const handleChangePage = (_: unknown, newPage: number) => {
    fetchStats(newPage, pagination.limit, sortBy, sortOrder, startDate, endDate);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    fetchStats(0, newLimit, sortBy, sortOrder, startDate, endDate);
  };

  const handleSortByChange = (event: SelectChangeEvent) => {
    setSortBy(event.target.value);
  };

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as 'asc' | 'desc');
  };

  const getProductTypeLabel = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return t('checkout.productType.tutorial');
      case 'Workshop':
        return t('checkout.productType.workshop');
      case 'Item':
        return t('checkout.productType.item');
      case 'File':
        return t('checkout.productType.file');
      default:
        return type;
    }
  };

  const getProductTypeColor = (type: string) => {
    switch (type) {
      case 'Tutorial':
        return 'primary';
      case 'Workshop':
        return 'secondary';
      case 'Item':
        return 'success';
      case 'File':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return `₪${amount.toFixed(2)}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div>
        <Typography variant="h4" sx={{ mb: 3 }}>{t('admin.tabs.purchaseStats')}</Typography>
        
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('admin.purchaseStats.totalPurchases')}
                </Typography>
                <Typography variant="h4">
                  {summary.totalPurchases.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('admin.purchaseStats.totalRevenue')}
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(summary.totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('admin.purchaseStats.averageOrderValue')}
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(summary.averageOrderValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {t('admin.purchaseStats.uniqueProducts')}
                </Typography>
                <Typography variant="h4">
                  {summary.uniqueProducts.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <DatePicker
            label={t('admin.purchaseStats.startDate')}
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{
              textField: {
                size: 'small'
              }
            }}
          />
          <DatePicker
            label={t('admin.purchaseStats.endDate')}
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{
              textField: {
                size: 'small'
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('admin.purchaseStats.sortBy')}</InputLabel>
            <Select
              value={sortBy}
              label={t('admin.purchaseStats.sortBy')}
              onChange={handleSortByChange}
            >
              <MenuItem value="totalPurchases">{t('admin.purchaseStats.sortByPurchases')}</MenuItem>
              <MenuItem value="totalRevenue">{t('admin.purchaseStats.sortByRevenue')}</MenuItem>
              <MenuItem value="averagePrice">{t('admin.purchaseStats.sortByAveragePrice')}</MenuItem>
              <MenuItem value="lastPurchaseDate">{t('admin.purchaseStats.sortByLastPurchase')}</MenuItem>
              <MenuItem value="title">{t('admin.purchaseStats.sortByTitle')}</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('admin.purchaseStats.sortOrder')}</InputLabel>
            <Select
              value={sortOrder}
              label={t('admin.purchaseStats.sortOrder')}
              onChange={handleSortOrderChange}
            >
              <MenuItem value="desc">{t('admin.purchaseStats.descending')}</MenuItem>
              <MenuItem value="asc">{t('admin.purchaseStats.ascending')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      
      {loading ? (
        <Typography>{t('admin.purchaseStats.loading')}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="purchase stats table">
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.purchaseStats.product')}</TableCell>
                  <TableCell>{t('admin.purchaseStats.productType')}</TableCell>
                  <TableCell>{t('admin.purchaseStats.totalPurchases')}</TableCell>
                  <TableCell>{t('admin.purchaseStats.totalRevenue')}</TableCell>
                  <TableCell>{t('admin.purchaseStats.averagePrice')}</TableCell>
                  <TableCell>{t('admin.purchaseStats.lastPurchase')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((stat) => (
                  <TableRow key={stat._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {stat.image && (
                          <img
                            src={stat.image}
                            alt={stat.title}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4
                            }}
                          />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', maxWidth: 200 }}>
                            {stat.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                            {stat.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getProductTypeLabel(stat.productType)} 
                        size="small" 
                        color={getProductTypeColor(stat.productType) as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {stat.totalPurchases}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(stat.totalRevenue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(stat.averagePrice)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(stat.lastPurchaseDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
          />
        </Paper>
      )}
      </div>
    </LocalizationProvider>
  );
};
